// Маппинг полномочий
export const PERMISSIONS = {
  VIEW_EMPLOYER: 'view_employers',
  VIEW_VACANCY: 'view_vacancy',
  VIEW_CANDIDATE: 'view_candidates',
  VIEW_CHAT: 'view_chat',
  VIEW_DASHBOARD: 'view_dashboard',
  ADD_MANAGER: 'add_manager',
  ADD_ANY_VACANCY: 'add_any_vacancy',
  ADD_ANY_HR: 'add_any_hr',
};

// Виртуальные permission-ключи только для UI.
// Нужны, чтобы одинаковые backend-права не связывали разные чекбоксы между собой.
export const UI_PERMISSION_ALIASES = {
  view_employer_for_vacancy_employer_data: 'view_employer',
  view_vacancy_employer_for_vacancy_employer_data: 'view_vacancy_employer',
  change_vacancy_broadcast_for_moderation_action: 'change_vacancy_broadcast',
};

export const resolvePermissionForApi = (permission) => UI_PERMISSION_ALIASES[permission] || permission;

// Конфигурация защищенных роутов
export const PROTECTED_ROUTES = {
  '/contractor': PERMISSIONS.VIEW_EMPLOYER,
  '/vacancy': PERMISSIONS.VIEW_VACANCY,
  '/applicants': PERMISSIONS.VIEW_CANDIDATE,
  '/chats': PERMISSIONS.VIEW_CHAT,
  '/': PERMISSIONS.VIEW_DASHBOARD,
  '/add/manager': PERMISSIONS.ADD_MANAGER,
};

export const PERMISSION_GROUPS = [
  {
    title: 'Основные полномочия',
    items: [
      { label: 'Просмотр дашборда', permissions: [PERMISSIONS.VIEW_DASHBOARD] },
      { label: 'Просмотр контрагентов', permissions: [PERMISSIONS.VIEW_EMPLOYER] },
      { label: 'Просмотр соискателей', permissions: [PERMISSIONS.VIEW_CANDIDATE] },
      { label: 'Просмотр чатов', permissions: [PERMISSIONS.VIEW_CHAT] },
      { label: 'Добавление менеджера', permissions: [PERMISSIONS.ADD_MANAGER] },
    ],
  },
  {
    title: 'Вакансии',
    items: [
      { label: 'Полный доступ', permissions: ['view_any_vacancy'], exclusiveGroup: 'vacancy-access-level' },
      {
        label: 'Ограниченный доступ',
        permissions: ['view_vacancy'],
        exclusiveGroup: 'vacancy-access-level',
        children: [
          {
            label: 'Активные',
            permissions: ['view_active_vacancy'],
            children: [
              {
                label: 'Действия',
                noChildrenIndent: true,
                children: [
                  { label: 'Редактировать', permissions: ['change_vacancy'] },
                  { label: 'Удалить', permissions: ['change_vacancy_active'] },
                  { label: 'Деактивировать', permissions: ['change_vacancy_broadcast_for_moderation_action'] },
                ],
              },
            ],
          },
          {
            label: 'На модерации',
            permissions: ['view_moderation_vacancy', 'view_moderation', 'view_vacancy_employer', 'view_moderation_action'],
            children: [
              {
                label: 'Действия',
                noChildrenIndent: true,
                children: [
                  { label: 'Редактировать', permissions: ['change_moderation', 'reset_moderation'] },
                  { label: 'Отправить', permissions: ['send_moderation'] },
                  {
                    label: 'Разместить',
                    permissions: ['change_vacancy_broadcast_for_moderation_action'],
                    exclusiveGroup: 'moderation-approval-action',
                    exclusivePriority: 2,
                    children: [
                      { label: 'Полный доступ транслирования', permissions: ['change_vacancy_full_broadcast'], exclusiveGroup: 'vacancy-broadcast-access-level' },
                      { label: 'Ограниченный доступ транслирования', permissions: ['change_vacancy_limited_broadcast'], exclusiveGroup: 'vacancy-broadcast-access-level' },
                    ],
                  },
                  { label: 'Согласовать', permissions: ['accept_moderation'], exclusiveGroup: 'moderation-approval-action', exclusivePriority: 1 },
                  { label: 'Чекбокс регистрации', permissions: ['change_vacancy_skip_verification'] },
                ],
              },
              { label: 'История вакансии', permissions: ['view_vacancy_history'] },
            ],
          },
          {
            label: 'Неактивные',
            permissions: ['view_inactive_vacancy'],
            children: [
              {
                label: 'Действия',
                noChildrenIndent: true,
                children: [
                  { label: 'Редактировать', permissions: ['change_vacancy'] },
                  { label: 'Удалить', permissions: ['change_vacancy_active'] },
                  { label: 'Отправить на модерацию', permissions: ['renew_moderation'] },
                ],
              },
            ],
          },
          {
            label: 'Удаленные',
            permissions: ['view_deleted_vacancy'],
            children: [
              {
                label: 'Действия',
                noChildrenIndent: true,
                children: [
                  { label: 'Редактировать', permissions: ['change_vacancy'] },
                  { label: 'Восстановить', permissions: ['change_vacancy_active'] },
                  { label: 'Удалить безвозвратно', permissions: ['delete_vacancy'] },
                ],
              },
            ],
          },
          {
            label: 'Менеджер',
            children: [
              { label: 'Ограниченные (только просмотр менеджера)', permissions: ['view_vacancy_manager'], exclusiveGroup: 'vacancy-manager-access-level' },
              { label: 'Полные (просмотр, назначение и смена менеджера)', permissions: ['change_vacancy_manager', 'view_vacancy_manager'], exclusiveGroup: 'vacancy-manager-access-level' },
            ],
          },
          {
            label: 'Отклики',
            children: [
              { label: 'Ограниченные (без просмотра анкет)', permissions: ['view_application'], exclusiveGroup: 'vacancy-application-access-level' },
              { label: 'Полные (с просмотром анкет)', permissions: ['view_application', 'view_full_application', 'view_candidate'], exclusiveGroup: 'vacancy-application-access-level' },
            ],
          },
          {
            label: 'Данные контрагента',
            children: [
              {
                label: 'Ограниченные данные (Название, ИНН)',
                permissions: ['view_employer_for_vacancy_employer_data', 'view_vacancy_employer_for_vacancy_employer_data'],
                exclusiveGroup: 'vacancy-employer-access-level',
              },
              {
                label: 'Полные данные (Название, ИНН, телефон, email, ссылка на профиль)',
                permissions: ['view_full_employer', 'view_vacancy_employer_for_vacancy_employer_data'],
                exclusiveGroup: 'vacancy-employer-access-level',
              },
            ],
          },
        ],
      },
      {
        label: 'Создать вакансию',
        children: [
          { label: 'Полные (создание вакансии любому контрагенту)', permissions: ['add_any_vacancy'], exclusiveGroup: 'create-vacancy-access-level' },
          { label: 'Ограниченные (только делегированным контрагентам)', permissions: ['add_vacancy'], exclusiveGroup: 'create-vacancy-access-level' },
        ],
      },
      {
        label: 'Создать поиск и подбор',
        children: [
          { label: 'Полные (создание поиска и подбора любому контрагенту)', permissions: ['add_any_hr'], exclusiveGroup: 'create-hr-access-level' },
          { label: 'Ограниченные (только делегированным контрагентам)', permissions: ['add_hr'], exclusiveGroup: 'create-hr-access-level' },
        ],
      },
      {
        label: 'Лимиты контрагентов',
        permissions: ['change_user_employers', 'change_user_max_employers', 'change_user_auto_employers', 'change_user_supervisor'],
      },
    ],
  },
];

const collectPermissionNames = (items, set = new Set()) => {
  items.forEach((item) => {
    if (item.permissions) {
      item.permissions.forEach((permission) => set.add(permission));
    }
    if (item.children) {
      collectPermissionNames(item.children, set);
    }
  });
  return set;
};

const collectPermissionLabels = (items, labels = {}) => {
  items.forEach((item) => {
    if (item.permissions) {
      item.permissions.forEach((permission) => {
        if (!labels[permission]) {
          labels[permission] = item.label;
        }
      });
    }
    if (item.children) {
      collectPermissionLabels(item.children, labels);
    }
  });
  return labels;
};

export const DISPLAY_PERMISSIONS = Array.from(collectPermissionNames(PERMISSION_GROUPS.flatMap((group) => group.items)));
export const PERMISSION_LABELS = collectPermissionLabels(PERMISSION_GROUPS.flatMap((group) => group.items));
